import React, { useState, useEffect } from "react";
import { useAuth } from "../../context/AuthContext";
import { X, Send, Trash2, MessageCircle } from "lucide-react";

const formatDate = (dateString) => {
  const date = new Date(dateString);
  const now = new Date();
  const diffMs = now - date;
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);

  if (diffMins < 1) return "À l'instant";
  if (diffMins < 60) return `Il y a ${diffMins} min`;
  if (diffHours < 24) return `Il y a ${diffHours}h`;
  if (diffDays < 7) return `Il y a ${diffDays}j`;
  
  return date.toLocaleDateString('fr-FR', { 
    day: 'numeric', 
    month: 'short',
    year: date.getFullYear() !== now.getFullYear() ? 'numeric' : undefined
  });
};

const CommentsPanel = ({ item, onClose }) => {
  const { api, user } = useAuth();
  const [comments, setComments] = useState([]);
  const [text, setText] = useState("");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get(`/content/${item.id}/notes`)
      .then((res) => setComments(res.data))
      .finally(() => setLoading(false));
  }, [api, item.id]);

  const addComment = async () => {
    if (!text.trim()) return;
    try {
      const res = await api.post(`/content/${item.id}/notes`, { text: text.trim() });
      setComments((prev) => [res.data, ...prev]);
      setText("");
    } catch {}
  };

  const deleteComment = async (commentId) => {
    try {
      await api.delete(`/notes/${commentId}`);
      setComments((prev) => prev.filter((c) => c.id !== commentId));
    } catch {}
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-end" onClick={onClose}>
      <div className="absolute inset-0 bg-black/50 backdrop-blur-[2px]" />
      <div
        className="relative h-full w-[420px] bg-[#0a0a0c] border-l border-white/[0.06] flex flex-col shadow-2xl"
        onClick={(e) => e.stopPropagation()}
        style={{ animation: "slideInRight 0.25s ease-out" }}
        data-testid="comments-panel"
      >
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-white/[0.06] shrink-0">
          <div className="flex items-center gap-2.5">
            <div className="w-8 h-8 rounded-lg bg-white/[0.05] flex items-center justify-center">
              <MessageCircle size={16} className="text-white/60" />
            </div>
            <div>
              <h3 className="text-white font-semibold text-sm">Commentaires</h3>
              <p className="text-[#444] text-[11px] truncate max-w-[260px] mt-0.5">{item.title}</p>
            </div>
          </div>
          <button onClick={onClose} className="text-[#555] hover:text-white transition-colors">
            <X size={18} />
          </button>
        </div>

        {/* Comments list */}
        <div className="flex-1 overflow-y-auto p-5 space-y-3">
          {loading ? (
            <div className="flex items-center justify-center py-10">
              <div className="w-5 h-5 border-2 border-white/10 border-t-white/50 rounded-full animate-spin" />
            </div>
          ) : comments.length === 0 ? (
            <div className="text-center py-10">
              <MessageCircle size={32} className="mx-auto text-white/10 mb-3" />
              <p className="text-[#444] text-sm">Aucun commentaire</p>
              <p className="text-[#333] text-[11px] mt-1">Soyez le premier à commenter</p>
            </div>
          ) : (
            comments.map((comment) => (
              <div key={comment.id} className="group rounded-xl bg-white/[0.03] border border-white/[0.05] p-4">
                {/* Comment header with user info */}
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-2">
                    <div className="w-6 h-6 rounded-full bg-white/[0.08] flex items-center justify-center text-[10px] text-white/60 font-medium">
                      {(comment.username || "U")[0].toUpperCase()}
                    </div>
                    <span className="text-[12px] text-white/80 font-medium">
                      {comment.username || "Utilisateur"}
                    </span>
                    <span className="text-[10px] text-[#444]">•</span>
                    <span className="text-[10px] text-[#444]">
                      {formatDate(comment.created_at)}
                    </span>
                  </div>
                  {comment.user_id === user?.id && (
                    <button
                      onClick={() => deleteComment(comment.id)}
                      className="opacity-0 group-hover:opacity-100 text-[#444] hover:text-red-400/80 transition-all p-1"
                      title="Supprimer"
                    >
                      <Trash2 size={12} />
                    </button>
                  )}
                </div>
                {/* Comment text */}
                <p className="text-[#bbb] text-[13px] leading-relaxed pl-8">{comment.text}</p>
              </div>
            ))
          )}
        </div>

        {/* Add comment */}
        <div className="px-5 py-4 border-t border-white/[0.06] shrink-0">
          <div className="flex items-start gap-2">
            <div className="w-8 h-8 rounded-full bg-white/[0.08] flex items-center justify-center text-[11px] text-white/60 font-medium shrink-0 mt-0.5">
              {(user?.username || "U")[0].toUpperCase()}
            </div>
            <div className="flex-1 flex items-center gap-2">
              <input
                value={text}
                onChange={(e) => setText(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && addComment()}
                placeholder="Ajouter un commentaire..."
                className="flex-1 bg-white/[0.03] border border-white/[0.07] rounded-lg px-3.5 py-2.5 text-[13px] text-white placeholder:text-[#333] focus:outline-none focus:border-white/[0.15] transition-colors"
                data-testid="comment-input"
              />
              <button
                onClick={addComment}
                disabled={!text.trim()}
                className="w-10 h-10 rounded-lg bg-white/[0.05] border border-white/[0.07] flex items-center justify-center text-[#555] hover:text-white hover:bg-white/[0.08] transition-all disabled:opacity-30"
                data-testid="send-comment-btn"
              >
                <Send size={15} />
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CommentsPanel;
